import React, { useEffect, useState } from 'react';
import { FileText, FileDown, Loader2, Filter, Sparkles, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const CatalogPDF = () => {
  const [categories, setCategories] = useState([]);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState({});

  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [handmadeOnly, setHandmadeOnly] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/catalog-pdf/categories');
        setCategories(res.data.categories || []);
        setSubcategoriesByCategory(res.data.subcategories || {});
      } catch (_) { /* silent */ }
    })();
  }, []);

  const subcategoryOptions = category ? (subcategoriesByCategory[category] || []) : [];

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (subcategory) params.set('subcategory', subcategory);
      if (minPrice) params.set('min_price', minPrice);
      if (maxPrice) params.set('max_price', maxPrice);
      if (handmadeOnly) params.set('handmade_only', 'true');
      if (includeInactive) params.set('include_inactive', 'true');
      if (title) params.set('title', title);
      if (intro) params.set('intro', intro);

      const res = await axiosInstance.get(`/catalog-pdf/generate?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const slug = (category || 'alle').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 40);
      link.setAttribute('download', `homeyoursoul-katalog-${slug}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Katalog-PDF heruntergeladen');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Generierung fehlgeschlagen';
      toast.error(typeof detail === 'string' ? detail : 'Generierung fehlgeschlagen');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen p-3 sm:p-6" style={{ backgroundColor: '#F5EDE4' }} data-testid="catalog-pdf-page">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#E8DCC8]">
            <BookOpen className="h-6 w-6 text-[#5D4E3C]" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#5D4E3C]">Katalog-PDF</h1>
            <p className="text-xs sm:text-sm text-[#8B7355]">
              Erstelle einen gefilterten Produktkatalog als PDF zum Versenden oder Ausdrucken
            </p>
          </div>
        </div>

        <Card className="rounded-none border-[#D4C4B0]">
          <CardHeader className="border-b border-[#E8DCC8]">
            <CardTitle className="text-[#5D4E3C] flex items-center gap-2">
              <Filter className="h-5 w-5" /> Filter
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#5D4E3C]">Kategorie</Label>
                <select
                  value={category}
                  onChange={(e) => { setCategory(e.target.value); setSubcategory(''); }}
                  className="mt-1 w-full border border-[#D4C4B0] bg-white px-3 py-2 text-sm rounded-none"
                  data-testid="catalog-category-select"
                >
                  <option value="">— alle Kategorien —</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[#5D4E3C]">Unterkategorie</Label>
                <select
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                  disabled={!category}
                  className="mt-1 w-full border border-[#D4C4B0] bg-white px-3 py-2 text-sm rounded-none disabled:bg-gray-100 disabled:text-gray-400"
                  data-testid="catalog-subcategory-select"
                >
                  <option value="">— alle —</option>
                  {subcategoryOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#5D4E3C]">Preis ab (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="rounded-none border-[#D4C4B0] mt-1"
                  data-testid="catalog-min-price"
                />
              </div>
              <div>
                <Label className="text-[#5D4E3C]">Preis bis (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="999,99"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="rounded-none border-[#D4C4B0] mt-1"
                  data-testid="catalog-max-price"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <label className="flex items-center gap-2 text-sm text-[#5D4E3C] cursor-pointer">
                <input
                  type="checkbox"
                  checked={handmadeOnly}
                  onChange={(e) => setHandmadeOnly(e.target.checked)}
                  data-testid="catalog-handmade-only"
                />
                Nur handgefertigte Produkte
              </label>
              <label className="flex items-center gap-2 text-sm text-[#5D4E3C] cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  data-testid="catalog-include-inactive"
                />
                Auch inaktive einbeziehen
              </label>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-none border-[#D4C4B0]">
          <CardHeader className="border-b border-[#E8DCC8]">
            <CardTitle className="text-[#5D4E3C] flex items-center gap-2">
              <Sparkles className="h-5 w-5" /> Personalisierung (optional)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-4">
            <div>
              <Label className="text-[#5D4E3C]">Katalog-Titel</Label>
              <Input
                placeholder="z.B. Frühlings-Kollektion 2026"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="rounded-none border-[#D4C4B0] mt-1"
                data-testid="catalog-title-input"
              />
            </div>
            <div>
              <Label className="text-[#5D4E3C]">Einleitungstext</Label>
              <Textarea
                rows={3}
                placeholder="Optional: kurze Einleitung, die oben im Katalog erscheint"
                value={intro}
                onChange={(e) => setIntro(e.target.value)}
                className="rounded-none border-[#D4C4B0] mt-1"
                data-testid="catalog-intro-input"
              />
            </div>
          </CardContent>
        </Card>

        <Button
          onClick={handleGenerate}
          disabled={generating}
          className="w-full rounded-none bg-[#5D4E3C] hover:bg-[#4A3E2F] text-white py-4 text-base"
          data-testid="catalog-generate-btn"
        >
          {generating ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <FileDown className="h-5 w-5 mr-2" />}
          {generating ? 'Wird erstellt…' : 'Katalog-PDF herunterladen'}
        </Button>
      </div>
    </div>
  );
};

export default CatalogPDF;
